/**
 * THE sanitiser. One implementation, every runtime.
 *
 * docs/telemetry.md §16.1 says "one sanitiser, one place". §22 then prescribed four:
 * `cleanAttributes()` in packages/app, its copy in packages/terramantle.dev, and "new equivalents"
 * in the Worker and in scanner-runtime. Four copies of the function that decides whether a secret
 * reaches a log store is four chances to get it wrong, and the browser pair had already drifted.
 *
 * This is a superset of the behaviour the browser shipped (drop undefined/empty, truncate to 512),
 * so re-pointing `cleanAttributes()` at it keeps every existing test passing and adds the three
 * rules it was missing: the key denylist, URL query stripping, and refusing objects.
 *
 * Zero dependencies and no Node built-ins, because this file runs in a browser bundle, a Cloudflare
 * isolate, a Node pod and the supervisor's raw-TS loader.
 */

/** §16.4: every string attribute is truncated to this. Matches the browser's existing cap. */
export const MAX_VALUE_LENGTH = 512

/**
 * §16.5: the only things an attribute may be.
 *
 * The type is the point. "Reject objects" as a review rule gets forgotten; as a signature it is a
 * compile error. A parsed plan, a findings array or a policy input document cannot be passed here
 * without a cast, and a cast is visible in review in a way that a plausible-looking object is not.
 */
export type AttrValue = string | number | boolean | readonly string[] | readonly number[] | readonly boolean[]

export type AttrBag = Record<string, AttrValue | null | undefined>

/**
 * §16.2: key substrings that cause the attribute to be DROPPED, case-insensitively.
 *
 * Dropped, not redacted to `***`: a redacted key still tells an attacker the field exists and
 * roughly where to look.
 *
 * `lease` is here beyond §16's list. A Cloudflare queue lease id is a bearer capability to ack or
 * retry a message, and the queue supervisor used to log it verbatim into Loki - so the denylist has
 * to know the name, not just the category.
 */
export const DENIED_KEY_SUBSTRINGS: readonly string[] = [
  'token',
  'secret',
  'password',
  'passwd',
  'authorization',
  'cookie',
  'credential',
  'api_key',
  'apikey',
  // R2_ACCESS_KEY_ID and OBJECTSTORE_ACCESS_KEY_ID are both on §16's named-secret table and were
  // caught by nothing: `api_key` does not match `access_key_id`. Deliberately NOT the broader
  // `_key`, which would also drop `tm.storage.key` - an object key §6 explicitly asks for.
  'access_key',
  'client_secret',
  'signature',
  'private_key',
  'privatekey',
  'passphrase',
  'bearer',
  'lease',
]

/**
 * Keys that LOOK denied but are explicitly allowed.
 *
 * `tm.gate.*` and the hashes are the cases that matter: `attestation_hash` is a sha-256 over the
 * redacted canonical plan and is the primary key a CI apply job checks against, and
 * `source_tree_hash` / `source_bundle_sha256` are content digests. All three are safe and genuinely
 * useful, and all three would otherwise be caught by nothing - but `tm.build.signature_algorithm`
 * and friends would be, so the allowlist exists for when one appears.
 */
export const ALLOWED_KEYS: readonly string[] = [
  'tm.plan.attestation_hash',
  'tm.build.source_tree_hash',
  'tm.build.source_bundle_sha256',
]

let truncationCount = 0
let droppedKeyCount = 0

/**
 * §16.4: "a truncated attribute is a bug signal - log a counter, do not silently grow."
 *
 * Exposed as counters rather than a log line so the caller decides: the Worker turns them into a
 * metric, tests assert on them, and nothing has to emit from inside the sanitiser (which would be a
 * re-entrancy hazard, since a logger calls this).
 */
export function sanitiserStats(): { truncated: number; droppedKeys: number } {
  return { truncated: truncationCount, droppedKeys: droppedKeyCount }
}

/** Test seam. */
export function resetSanitiserStats(): void {
  truncationCount = 0
  droppedKeyCount = 0
}

/** True when the key matches the §16.2 denylist and is not explicitly allowed. */
export function isDeniedKey(key: string): boolean {
  if (ALLOWED_KEYS.includes(key)) return false
  const lower = key.toLowerCase()
  return DENIED_KEY_SUBSTRINGS.some((needle) => lower.includes(needle))
}

function truncate(value: string): string {
  if (value.length <= MAX_VALUE_LENGTH) return value
  truncationCount += 1
  return value.slice(0, MAX_VALUE_LENGTH)
}

function cleanValue(value: AttrValue): AttrValue | undefined {
  if (typeof value === 'string') return value === '' ? undefined : truncate(value)
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value === 'boolean') return value
  if (Array.isArray(value)) {
    // A homogeneous array of scalars is a legitimate attribute (OTLP supports it). Anything else -
    // notably an array of objects - is the design error §16.5 makes unrepresentable, and a runtime
    // guard is kept anyway for untyped call sites reached through `any`.
    const out = value.filter(
      (v) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean',
    )
    if (out.length === 0) return undefined
    return out.map((v) => (typeof v === 'string' ? truncate(v) : v)) as AttrValue
  }
  return undefined
}

/**
 * Sanitise an attribute bag.
 *
 * Drops: denylisted keys, `undefined`, `null`, empty strings, non-finite numbers, objects.
 * Truncates: every string to 512 chars.
 *
 * Never throws. A sanitiser that can throw is a sanitiser that gets wrapped in a bare try/catch
 * which then swallows the attributes entirely, so the failure mode is "no telemetry" rather than
 * "one bad attribute".
 */
export function sanitiseAttributes(attrs: AttrBag): Record<string, AttrValue> {
  const out: Record<string, AttrValue> = {}
  try {
    for (const [key, raw] of Object.entries(attrs)) {
      if (raw === undefined || raw === null) continue
      if (isDeniedKey(key)) {
        droppedKeyCount += 1
        continue
      }
      const value = cleanValue(raw)
      if (value !== undefined) out[key] = value
    }
  } catch {
    /* never let the sanitiser be the thing that breaks a caller */
  }
  return out
}

/**
 * Strip a URL down to what is safe to emit as `url.full`.
 *
 * §16.3: "strip every URL query string ... allowlist, not denylist: no query parameter is emitted
 * unless explicitly listed, and today none is."
 *
 * This is the single most likely accidental leak in the whole rollout. `r2Presign.ts` and
 * `archivePresign.ts` produce URLs carrying `X-Amz-Signature` and `X-Amz-Credential` in the query
 * string, and `@opentelemetry/instrumentation-undici` sets `url.full` from the full URL by default -
 * so a naive auto-instrumentation rollout publishes object-store credentials to the trace store.
 *
 * The fragment goes too: the portal's OIDC flow can put state in it.
 */
export function sanitiseUrl(url: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    // Not a parseable absolute URL (a relative path, or something malformed). Cut at the first `?`
    // or `#` rather than returning it unchanged - the whole point is that a query never survives.
    const cut = url.search(/[?#]/)
    return truncate(cut === -1 ? url : url.slice(0, cut))
  }
}

/**
 * True when no value in the bag looks like a leaked URL query or credential.
 *
 * Exported for TESTS, not for the emit path. §16.7 asks for "a test that walks a captured span's
 * attributes and fails on a denylisted key or a `?` in a URL"; this is that assertion, shared so the
 * Worker, the pods and the browser all check the same thing rather than three near-miss versions.
 */
export function findPrivacyViolations(attrs: Record<string, unknown>): string[] {
  const problems: string[] = []
  for (const [key, value] of Object.entries(attrs)) {
    if (isDeniedKey(key)) problems.push(`denylisted key: ${key}`)
    if (typeof value !== 'string') continue
    if (key === 'url.full' || key === 'url.path' || key.endsWith('.url')) {
      if (value.includes('?') || value.includes('#')) problems.push(`query/fragment in ${key}`)
    }
    if (/X-Amz-(Signature|Credential|Security-Token)/i.test(value)) {
      problems.push(`presigned-URL credential in ${key}`)
    }
    if (/\bBearer\s+\S/i.test(value)) problems.push(`bearer token in ${key}`)
    if (/^eyJ[A-Za-z0-9_-]{10,}\./.test(value)) problems.push(`JWT/JWE in ${key}`)
  }
  return problems
}

/**
 * The same values, with MUTABLE arrays — structurally assignable to `@opentelemetry/api`'s
 * `Attributes`.
 *
 * Note the homogeneous array union rather than `Array<string | number | boolean>`: OTel's
 * `AttributeValue` is a union of per-type arrays, so a mixed array is not assignable to it.
 */
export type MutableAttrValue = string | number | boolean | string[] | number[] | boolean[]

/**
 * Bridge sanitised attributes to the shape the OTel API wants.
 *
 * `AttrValue` types arrays as `readonly` deliberately, so a caller cannot hand over a live array it
 * then mutates — but `@opentelemetry/api` types them mutable, so every `startSpan`/`addEvent` call
 * needs a copy. This lives here rather than in each consumer because the Worker and both browser
 * packages all need it, and because keeping it here means `@opentelemetry/api` is NOT a dependency of
 * this package — which is the constraint that lets it be vendored into the scanner images.
 */
export function toMutableAttributes(attrs: Record<string, AttrValue>): Record<string, MutableAttrValue> {
  const out: Record<string, MutableAttrValue> = {}
  for (const [k, v] of Object.entries(attrs)) {
    out[k] = Array.isArray(v) ? ([...v] as MutableAttrValue) : (v as MutableAttrValue)
  }
  return out
}
